import { BookingSplitRow, ServiceAssignment, ServiceItem, ServiceTransaction } from '../types';
import { bookingIsFlatProof, inspectionFlatTarget, isFlatRateService, resolveFlatCoverage } from './flatRateLogic';
import { isNepService } from './nepPosition';
import { isAccountingEntry } from './serviceLogUtils';
import { isHourlyUnit, readMinHoursInput } from './serviceUnits';
import { hatBestandteile } from './bestandteile/bestandteilLogic';
import { istBuchungDerTochter, istKindBuchung } from './linkedBillingMode';

/**
 * Regeln des Abrechnungsdialogs — bewusst als pure Funktionen neben der
 * Komponente.
 *
 * WARUM: BillingSelectionModal ist ein duenner Container (siehe die stehende
 * Entscheidung „Logik in Hooks + pure Funktionen"), und das Projekt hat keine
 * DOM-Testumgebung. Die tragenden Regeln — welche Zeile welche Menge bucht, wann
 * ein Mengenfeld ueberhaupt erscheint, wann bestaetigt werden darf — sind hier
 * ohne jsdom pruefbar.
 *
 * Fachlicher Hintergrund: eine Begehung kann mehrere LOSE abdecken. Es entsteht
 * genau EIN Bericht, die erbrachte Leistung muss aber je Los auf eine eigene
 * LV-Position gebucht werden, damit sie auf der Rechnung richtig steht.
 */

/**
 * Module, die eine eigene LV-Position abrechnen — die drei Dokumentmodule, die
 * Begehung, die Einweisung in den SiGe-Plan und der A+S-Plan. Die Begehung ist
 * hier, seit auch ihr Buchungsdialog (EmailModal) ueber diese Regel geht statt
 * ueber die LV-Nummern-Praefixe `2.7.`/`2.8.`.
 *
 * Die Einweisung rechnet wie die Begehung ueber ein KONTINGENT ab (Einheit
 * „Stück", Standardposition 2.12.10 mit 3 Stück) und nicht wie die drei
 * Pauschalpositionen: auf einer Baustelle wird mehrfach eingewiesen.
 *
 * `asplan` (Plan Paketposition B5, Betreiber 19.10.2026): der A+S-Plan bietet
 * die Buchung direkt nach der Freigabe an. Er findet seine Position NUR ueber
 * die Zuordnung `asplan` — sein Textarm ist leer (siehe
 * BILLING_TITLE_KEYWORDS), und die Sperre des Textarms der anderen Module
 * durch die Zuordnung `asplan` bleibt (siehe `isUnassigned`).
 */
export type BillingModule = Extract<ServiceAssignment, 'vorank' | 'unterlage' | 'sigeplan' | 'inspection' | 'einweisung' | 'asplan'>;

/**
 * SCHLUESSELWOERTER — zweites Signal fuer Positionen OHNE Zuordnung.
 *
 * WARUM so knapp: sie duerfen NIE eine fremde Position einfangen. Ein echtes
 * Kunden-LV nennt seine Begehungspositionen z. B. „Betreuung Baustellen
 * Leitungsbau Los 1 LHT" — dort steht weder „Begehung" noch sonst ein Wort
 * dieser Listen. Genau deshalb ist die Zuordnung (`assignment`) die primaere
 * Regel und Text nur das zweite Signal.
 */
const BILLING_TITLE_KEYWORDS: Record<BillingModule, readonly string[]> = {
    // faengt „Vorankündigung", „Vorankuendigung", „Vorank." in jeder Beugung
    vorank: ['vorank'],
    // faengt „Unterlage", „Unterlagen", „Unterlage für spätere Arbeiten"
    unterlage: ['unterlage'],
    // „SiGe-Plan", „SiGePlan", „SiGe Plan" und die ausgeschriebene Form
    sigeplan: ['sige-plan', 'sigeplan', 'sige plan', 'sicherheits- und gesundheitsschutzplan'],
    /**
     * Nur „begehung" — faengt „Baustellenbegehung", „Sicherheitsbegehung",
     * „SiGe-Begehung", „Begehungen". Genau der Textarm, den EmailModal schon
     * immer hatte.
     *
     * „betreuung" steht hier BEWUSST NICHT, obwohl das echte Kunden-LV seine
     * Begehungen so nennt: dort tragen die Positionen `assignment: 'inspection'`
     * und werden bereits ueber die Zuordnung gefunden. Als Stichwort wuerde
     * „betreuung" dagegen die sicherheitstechnische Betreuung (SiFa/DGUV V2)
     * einfangen, die keine Begehung ist. Und im Begehungsdialog waehlt
     * EmailModal eine EINZIGE Trefferposition automatisch vor — ein
     * Falschtreffer waere dort keine Auswahl, sondern eine stille Fehlbuchung.
     */
    inspection: ['begehung'],
    /**
     * Nur „einweisung" — faengt „Einweisung", „Einweisungen", „SiGe-Plan-
     * Einweisung", „Erstunterweisung/Einweisung der Beschaeftigten".
     *
     * „unterweisung" steht hier BEWUSST NICHT, und zwar aus demselben Grund wie
     * „betreuung" eine Zeile hoeher: es faengt die sicherheitstechnische
     * UNTERWEISUNG der Fachkraft fuer Arbeitssicherheit (DGUV V2) ein, die
     * keine Einweisung in den SiGe-Plan ist, sondern eine voellig andere
     * Leistung mit eigener Position. Wo ein Kunden-LV seine Einweisung
     * abweichend nennt, traegt die Position `assignment: 'einweisung'` und wird
     * bereits ueber die Zuordnung gefunden — der Textarm ist nur die
     * Rueckfallebene fuer den Altbestand OHNE Zuordnung, und dort darf er
     * lieber nichts finden als das Falsche.
     *
     * Dass „unterweisung" das Wort „einweisung" nicht enthaelt, ist Absicht und
     * kein Zufall: die Pruefung laeuft ueber `includes`, ein Stichwort
     * „weisung" wuerde beide fangen.
     */
    einweisung: ['einweisung'],
    /**
     * LEER, mit Absicht: „Plan", „A+S" oder „Sicherheitsplan" treffen die
     * SiGe-Plan-Position direkt daneben (dieselbe Begruendung wie in
     * serviceTypeMatch.istLeistungVomTyp). Der A+S-Plan bucht nur auf eine
     * Position, die ein Mensch ausdruecklich auf `asplan` gestellt hat — eine
     * unklassifizierte Altposition bietet er nie an.
     */
    asplan: [],
};

/**
 * Eine Zuordnung, die eine konkrete Funktion benennt, SPERRT den Textarm: sie
 * ist die ausdrueckliche Aussage eines Menschen, wozu die Position gehoert, und
 * schlaegt jedes Wort im Titel. Unklassifiziert sind nur `'none'` und eine
 * fehlende Zuordnung — das ist der Altbestand, fuer den es den Textarm gibt.
 *
 * Das betrifft ALLE uebrigen Werte, auch `asplan`, `site_rules`,
 * `planning_general` und `additional`: „A+S-Plan", „Baustellenordnung",
 * „planungsbegleitend" und „Nachtrag" sagen genauso klar wie `inspection`,
 * dass die Position NICHT das Dokument dieses Moduls ist.
 *
 * `asplan` ist dabei der Fall, auf den es hier ankommt: eine Position
 * „Arbeits- und Sicherheitsplan erstellen" traegt das Wort „plan" und liegt
 * damit im Zugriff des SiGe-Plan-Textarms. Die Zuordnung sperrt ihn — sonst
 * boete der SiGe-Plan-Dialog die A+S-Plan-Position zur Buchung an.
 *
 * Seit Plan B5 (Betreiber 19.10.2026) BUCHT der A+S-Plan nach der Freigabe
 * und hat deshalb ein eigenes BillingModule `asplan`. Das aendert hier
 * nichts: die A+S-Plan-Position findet er ueber die ZUORDNUNG, sein eigener
 * Textarm ist leer, und fuer jedes andere Modul bleibt `asplan` eine fremde
 * Zuordnung, die den Textarm sperrt.
 */
const isUnassigned = (assignment: ServiceAssignment | undefined): boolean =>
    assignment === undefined || assignment === 'none';

const titleHitsModule = (title: string | undefined, module: BillingModule): boolean => {
    const t = (title || '').trim().toLowerCase();
    return t !== '' && BILLING_TITLE_KEYWORDS[module].some(k => t.includes(k));
};

/** Die drei Dokumentmodule, die eine Position ueber den Textarm an sich ziehen. */
const DOCUMENT_MODULES: readonly BillingModule[] = ['vorank', 'unterlage', 'sigeplan'];

/**
 * Welches Dokumentmodul eine UNKLASSIFIZIERTE Position allein ueber ihren
 * Titel an sich zieht — dieselbe Regel wie der Textarm von
 * selectBillableServices, beschraenkt auf Vorankuendigung, Unterlage und
 * SiGe-Plan. null bei ausdruecklicher Zuordnung oder ohne Treffer.
 *
 * WARUM: aus Alt-Rechnungen uebernommene Pauschalen („Honorar
 * Vorankündigung", „Honorar Erstellung SiGe-Plan") tragen `none`. Ihr Modul
 * bietet sie zur Buchung an — der Zusatzleistungs-Dialog beim Versand darf
 * sie deshalb nicht noch einmal anbieten (Vorfall 5634, Berichte #52/#53:
 * Vorankuendigung und SiGe-Plan je ein zweites Mal gebucht).
 *
 * Traegt der Titel zugleich „Begehung" oder „Einweisung", ist es KEIN
 * Dokument, sondern eine Leistung vor Ort („Einweisung in den SiGe-Plan",
 * „Begehung zur Kontrolle des SiGe-Plans") — dann null, damit sie dort
 * buchbar bleibt, wo sie bisher gebucht wurde.
 */
export const documentModuleByTitle = (s: Pick<ServiceItem, 'assignment' | 'title'>): BillingModule | null => {
    if (!isUnassigned(s.assignment)) return null;
    if (titleHitsModule(s.title, 'inspection') || titleHitsModule(s.title, 'einweisung')) return null;
    return DOCUMENT_MODULES.find(m => titleHitsModule(s.title, m)) ?? null;
};

export interface SelectBillableServicesOptions {
    /**
     * Wenn weder Zuordnung noch Titel etwas treffen: ALLE Leistungen anbieten,
     * statt eine leere Liste. Nur der SiGe-Plan-Editor tut das (Bestandsverhalten);
     * Vorankuendigung und Unterlage zeigen dann weiter nichts an — dort bleibt
     * dem Anwender die Zeile „Vorgang ohne Leistungsbuchung abschliessen".
     */
    fallbackToAllServices?: boolean;
    /**
     * Nur fuer ZAEHLSICHTEN, nie fuer einen Buchungsdialog: im Pauschalprojekt
     * die Begehungspositionen selbst statt der Pauschale liefern (Modul
     * 'inspection'). Wer fragt „welche Positionen SIND Begehungen" (etwa
     * inspectionBudget fuer den Nenner der Kachel), braucht sie weiterhin —
     * eine Pauschale in Einheit Monat sagt ueber Stueck oder Stunden nichts.
     */
    ignoreInspectionFlat?: boolean;
}

/**
 * DIE eine Regel, welche LV-Positionen ein Dokumentmodul zur Buchung anbietet.
 *
 * WARUM zentral und warum ohne Positionsnummern: die drei Module haben die
 * Auswahl frueher an fest verdrahteten LV-Nummern-Praefixen (`2.1.`, `1.4.`,
 * `1.2.` …) festgemacht. Die Nummerierung gehoert aber dem KUNDEN und ist von
 * Auftrag zu Auftrag verschieden — bei einem abweichend nummerierten LV bot der
 * Unterlage-Dialog die stuendliche Betreuung an und die Vorankuendigung die
 * SiGe-Plan-Position. Nummern sind damit als Kriterium unbrauchbar und kommen
 * hier ausdruecklich nicht mehr vor.
 *
 * Die Regel ist eine VEREINIGUNG, kein Wasserfall — genau wie die drei alten
 * Regeln, die sie ersetzt (der SiGe-Plan-Editor bot z. B. `assignment` ODER
 * Nummer ODER Titel an). Ein Wasserfall („Titel nur, wenn die Zuordnung nichts
 * findet") verlor die Fortschreibung: der Katalog liefert „SiGe-Plan erstellen"
 * mit `sigeplan`, eine im LV-Editor nachgetragene Zeile „SiGe-Plan
 * fortschreiben" bekommt aber `none` (AdminCatalogEditor) — sie war damit nicht
 * mehr buchbar und die falsche Zeile landete auf der Rechnung. Dasselbe Muster
 * bei „Aktualisierung der Vorankündigung" und „Fortschreibung der Unterlage".
 *
 * Eine Position ist dabei, wenn
 *  1. ihre ZUORDNUNG das Modul ist — die fachliche Wahrheit, ODER
 *  2. ihr TITEL ein Schluesselwort traegt UND sie unklassifiziert ist
 *     (`assignment` fehlt oder `'none'`). Eine ausdrueckliche fremde Zuordnung
 *     sperrt den Textarm: „Kontrolle der Einhaltung des SiGe-Plans" mit
 *     `assignment: 'inspection'` ist eine Begehung und gehoert NICHT in den
 *     SiGe-Plan-Dialog, auch wenn das Wort darin vorkommt.
 *  3. Bleibt die Vereinigung leer: optional ALLE Leistungen (siehe
 *     fallbackToAllServices).
 */
export const selectBillableServices = (
    services: ServiceItem[],
    module: BillingModule,
    options: SelectBillableServicesOptions = {},
): ServiceItem[] => {
    // NEP-Positionen (Eventualpositionen, nicht beauftragt) werden von KEINEM
    // Modul-Buchungsdialog angeboten — auch nicht ueber die Rueckfallebene.
    // Diese Dialoge buchen ohne Begruendung, und ohne Begruendung darf auf
    // eine nicht beauftragte Position nicht gebucht werden. Der einzige Weg
    // ist der Buchungsdialog im Projekt-Hub (ServiceTrackingModal), der sie
    // abfragt. Hier und nicht in jedem Aufrufer, weil der Versandweg der
    // Begehung sonst ueber die Vorauswahl (precapturedServiceId) am Filter
    // vorbeikam (Codex-Befund 18.09.2026).
    //
    // Monatspauschalen bietet NUR der Begehungsdialog an (als Nachweis, siehe
    // unten). Jeder andere Modul-Dialog bucht auf die gewaehlte Position eine
    // MENGE (der SiGe-Plan-Editor etwa +1) — auf einer Pauschale hiesse das ein
    // Monat Geld ohne Monatsschluessel FLAT-JJJJ-MM, und der Dialog zeigte dazu
    // noch den Nachweis-Hinweis. Auch nicht ueber die Rueckfallebene
    // (fallbackToAllServices), die sonst jede Position anbietet.
    //
    // PAKETPOSITIONEN (Positionen mit Bestandteilen, Plan Kernregel 7) bietet
    // KEIN Modul als direktes Buchungsziel an — auch nicht ueber die
    // Rueckfallebene. Ihre Buchung entsteht nur an der ausloesenden Handlung:
    // wenn der letzte Bestandteil erledigt ist, bucht sie der Abschlussschritt
    // (bestandteilBuchung.nachAusgabeBestandteile) bzw. der Dialog der
    // Bestandteile, sonst der Nachtlauf. Eine Modulbuchung von Hand darauf
    // hiesse „erbracht", waehrend Bestandteile offen sind — und der
    // Einmal-Riegel `BST-` griffe nicht. Nur die Zaehlsicht
    // (`ignoreInspectionFlat`) sieht sie weiter: sie fragt, welche Positionen
    // Begehungen SIND, nicht, worauf gebucht wird.
    const bookable = services.filter(s =>
        !isNepService(s)
        && (module === 'inspection' || !isFlatRateService(s))
        && (options.ignoreInspectionFlat === true || !hatBestandteile(s)));

    const selected = bookable.filter(s => {
        if (s.assignment === module) return true;
        if (!isUnassigned(s.assignment)) return false;
        return titleHitsModule(s.title, module);
    });

    // Pauschalprojekt (Betreiber 30.09.2026): der Versand eines Berichts bucht
    // auf die Pauschale, die GEDECKTE Begehungsposition (z. B. 2.7.10)
    // bekommt nichts mehr. Stuende sie daneben, waehlte die Vorauswahl
    // (last_billing_inspection) sie weiter, und der Versand buchte am Nachweis
    // vorbei auf eine Position, deren Geld ohnehin stillsteht.
    //
    // Eine Begehungsposition, die diese Pauschale NICHT deckt ('explicit' mit
    // nur einem Los), bleibt waehlbar — sonst waere das zweite Los im Versand
    // gar nicht mehr buchbar.
    if (module === 'inspection' && !options.ignoreInspectionFlat) {
        const flat = inspectionFlatTarget(bookable);
        if (flat) {
            const coverage = resolveFlatCoverage(bookable);
            const uncovered = selected.filter(s => !isFlatRateService(s) && coverage.get(s.id) !== flat.id);
            return [flat, ...uncovered];
        }
    }

    if (selected.length > 0) return selected;

    return options.fallbackToAllServices ? bookable : [];
};

/**
 * ALLE Positionen, die ein Modul-Dialog ueberhaupt anbieten darf — ohne
 * Zuordnungs- und Titelregel: ohne NEP, ohne Monatspauschale, ohne
 * Paketposition (Kernregel 7). Dieselbe Menge wie die Rueckfallebene
 * `fallbackToAllServices`, aber ohne den Umweg ueber ein Modul.
 *
 * Fuer das Handbuch (Plan B5): es hat keinen eigenen Zuordnungswert und bucht
 * deshalb nur ueber die Auswahl aus allen Positionen — eine Regel „welche ist
 * die Handbuch-Position" gaebe es nur ueber den Titel, und geraten wird nicht.
 */
export const alleBuchbarenPositionen = (services: readonly ServiceItem[] | null | undefined): ServiceItem[] =>
    (services || []).filter(s => !!s && !isNepService(s) && !isFlatRateService(s) && !hatBestandteile(s));

/**
 * Die Liste des Versanddialogs im Begehungsmodul (SiGeKoInspectionModule),
 * eigenes LV oder das der Mutter: selectBillableServices(…, 'inspection'),
 * aber nur mit Positionen, deren ZUORDNUNG 'inspection' ist.
 *
 * WARUM nicht selectBillableServices unveraendert: NEP-Filter und
 * Pauschalregel ([Pauschale, …Begehungspositionen, die sie NICHT deckt]) sind
 * dieselben, der Textarm (unklassifizierte Position mit „Begehung" im Titel)
 * war in diesem Dialog aber nie dabei. Mit ihm boete der Dialog auch in
 * Projekten OHNE Pauschale neue Positionen an, und ohne gemerkte Auswahl
 * waehlte pickInitialBillingIds (erste freie Position) womoeglich eine andere
 * vor. Ohne Pauschale bleibt die Liste so wie vorher: alle Nicht-NEP-Positionen
 * mit Zuordnung 'inspection'.
 */
export const selectInspectionSendServices = (services: ServiceItem[]): ServiceItem[] => {
    const flat = inspectionFlatTarget(services.filter(s => !isNepService(s)));
    return selectBillableServices(services, 'inspection')
        .filter(s => (flat !== null && s.id === flat.id) || s.assignment === 'inspection');
};

export interface BillingRowInput {
    serviceId: string | null;
    /**
     * ROHTEXT des Mengenfeldes. Er darf nie aus dem Modellwert zurueckgespeist
     * werden — sonst verschwindet jede Zwischenstufe beim Tippen ("2." wuerde
     * zu "2"). Dieselbe Regel wie bei den Mindeststunden, siehe serviceUnits.
     */
    amount: string;
}

export interface BillingRowEvaluation {
    serviceId: string | null;
    /** Position rechnet in Stunden ab (immer ueber isHourlyUnit, nie ueber den String). */
    hourly: boolean;
    /** Beschriftung des Mengenfeldes bzw. der Vorschau. */
    unitLabel: string;
    /** Mengenfeld sichtbar? */
    showAmount: boolean;
    /** Zahlenwert der Eingabe; 0 solange sie leer oder unfertig ist. */
    value: number;
    /** Menge, die tatsaechlich gebucht wird. */
    quantity: number;
    /** Ausdruecklich vereinbarte Mindeststunden dieser Position, sonst null. */
    minHours: number | null;
    /** Eingabe unterschreitet die Mindeststunden — nur eine Warnung, keine Sperre. */
    belowMin: boolean;
    /** Position steht schon in einer frueheren Zeile. */
    duplicate: boolean;
    valid: boolean;
    /**
     * Zeile liegt auf einer Monatspauschale: gebucht wird nur ein Nachweis
     * (Menge 0 im Protokoll, siehe flatRateLogic.planFlatProofEntry), kein
     * Mengenfeld. `quantity` ist dann 1 — nur das Signal „diese Zeile bucht",
     * aus dem book() in reportBooking die Nachweiszeile macht.
     */
    flatProof: boolean;
}

/**
 * Bewertet alle Zeilen des Dialogs auf einmal (die Regeln haengen voneinander
 * ab: „aufgeteilt" und „Position doppelt" sind Aussagen ueber die ganze Liste).
 *
 * DIE ZENTRALE MENGENREGEL:
 *  - EINE Zeile — der Normalfall und der gesamte Altbestand: das Mengenfeld
 *    erscheint nur bei Stundenpositionen, eine Stueckposition bucht genau 1.
 *  - MEHRERE Zeilen — die Aufteilung auf Lose: JEDE Zeile zeigt ein Mengenfeld
 *    und bucht die EINGETRAGENE Menge, unabhaengig von der Einheit. Genau das
 *    ist der Zweck der Aufteilung; eine Stueckposition liesse sich sonst nicht
 *    auf zwei Lose verteilen.
 *
 * Die Mindeststunden gelten JE ZEILE — jedes Los traegt sein eigenes Minimum.
 * Es gibt bewusst keine Regel ueber die Summe.
 *
 * AUSNAHME PAUSCHALE: eine Zeile auf einer Monatspauschale zeigt NIE ein
 * Mengenfeld, auch nicht in der Aufteilung. Sie bucht einen Nachweis und keine
 * Menge; eine eingetragene Zahl waere eine Angabe, die nirgends ankommt.
 */
export const evaluateBillingRows = (
    rows: BillingRowInput[],
    services: ServiceItem[],
): BillingRowEvaluation[] => {
    const isSplit = rows.length > 1;
    const firstUseOf = new Map<string, number>();

    return rows.map((row, index) => {
        const svc = row.serviceId ? services.find(s => s.id === row.serviceId) : undefined;
        const flatProof = bookingIsFlatProof(svc);
        const hourly = !flatProof && isHourlyUnit(svc?.unit);
        const parsed = readMinHoursInput(row.amount);
        const value = parsed.kind === 'value' ? parsed.value : 0;

        const showAmount = !flatProof && (isSplit || hourly);
        const quantity = showAmount ? value : 1;

        // defaultHoursForService liefert ohne Vereinbarung 1 zurueck — das ist
        // der Altbestand und kein Hinweis wert.
        const rawMin = svc?.minHours;
        const minHours =
            hourly && typeof rawMin === 'number' && Number.isFinite(rawMin) && rawMin > 0
                ? rawMin
                : null;

        const duplicate = row.serviceId !== null && firstUseOf.has(row.serviceId);
        if (row.serviceId !== null && !firstUseOf.has(row.serviceId)) {
            firstUseOf.set(row.serviceId, index);
        }

        // „Ohne Leistungsbuchung" gibt es nur als EINZIGE Zeile: wer aufteilt,
        // muss jeder Zeile ein Los zuordnen.
        const valid = row.serviceId === null
            ? !isSplit
            : !duplicate && (!showAmount || value > 0);

        return {
            serviceId: row.serviceId,
            hourly,
            unitLabel: flatProof ? 'Nachweis' : hourly ? 'Stunden' : (svc?.unit || 'Stück'),
            showAmount,
            value,
            quantity,
            minHours,
            belowMin: minHours !== null && value > 0 && value < minHours,
            duplicate,
            valid,
            flatProof,
        };
    });
};

/** Die tatsaechlich zu buchenden Zeilen — ohne „ohne Buchung" und ohne Dubletten. */
export const toBookingRows = (evaluations: BillingRowEvaluation[]): BookingSplitRow[] =>
    evaluations
        .filter(e => e.serviceId !== null && !e.duplicate && e.quantity > 0)
        .map(e => ({ serviceId: e.serviceId as string, quantity: e.quantity }));

/**
 * Summe aller Zeilen — reine Kontrollanzeige, es gibt keine Regel darauf.
 * Ein Nachweis auf der Pauschale ist keine Menge und zaehlt nicht mit (seine
 * 1 ist nur das Buchungssignal).
 */
export const totalBookedQuantity = (evaluations: BillingRowEvaluation[]): number =>
    toBookingRows(evaluations.filter(e => !e.flatProof)).reduce((sum, r) => sum + r.quantity, 0);

/**
 * Zuletzt genutzte Positionen aus `last_billing_<context>`.
 *
 * Der Schluessel trug bisher EINEN Positions-Schluessel im Klartext. Er wird
 * weiter gelesen (und bei einer einzelnen Zeile auch weiter so geschrieben),
 * damit ein Wechsel der Version in beide Richtungen folgenlos bleibt. Nur eine
 * Aufteilung wird als JSON-Liste abgelegt; eine aeltere Version findet dort
 * keine passende Position und faellt still auf ihre Vorauswahl zurueck.
 */
export const parseLastBillingValue = (raw: string | null | undefined): string[] => {
    if (!raw) return [];
    if (!raw.trimStart().startsWith('[')) return [raw];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string' && v !== '') : [];
    } catch {
        return [];
    }
};

/** Gegenstueck zu parseLastBillingValue. null = nichts zu merken. */
export const serializeLastBillingValue = (serviceIds: string[]): string | null => {
    if (serviceIds.length === 0) return null;
    if (serviceIds.length === 1) return serviceIds[0];
    return JSON.stringify(serviceIds);
};

/**
 * Vorauswahl beim Oeffnen: zuletzt genutzte Positionen, sonst der Kandidat des
 * Kontextes, sonst die erste freie Position. Gibt immer mindestens einen
 * Eintrag zurueck (null, wenn es gar nichts zu waehlen gibt).
 *
 * Die zuletzt genutzte AUFTEILUNG wird nur wiederhergestellt, wo Aufteilen
 * ueberhaupt erlaubt ist; die Mengen kommen NICHT aus dem Speicher, sondern je
 * Zeile aus defaultHoursForService (jede Begehung hat eigene Zeiten).
 */
export const pickInitialBillingIds = (
    services: ServiceItem[],
    context: string,
    blockedServices: Record<string, { reason: string }>,
    lastRaw: string | null | undefined,
    allowSplit: boolean,
    preferredIds: string[] = [],
): (string | null)[] => {
    if (services.length === 0) return [null];

    const available = services.filter(s => !blockedServices[s.id]);

    // Was das Projekt selbst zuletzt gebucht hat (vom Aufrufer aus dem
    // Protokoll gelesen) schlaegt den Geraetespeicher: es gilt auf jedem
    // Geraet und kennt die Wahl anderer Versender.
    const preferred = preferredIds.filter(id => available.some(s => s.id === id));
    if (preferred.length > 0) {
        return allowSplit ? preferred : [preferred[0]];
    }

    const remembered = parseLastBillingValue(lastRaw).filter(id => available.some(s => s.id === id));

    if (remembered.length > 0) {
        return allowSplit ? remembered : [remembered[0]];
    }

    let candidate: ServiceItem | undefined;
    if (context === 'vorank') {
        candidate = available.find(s => s.assignment === 'vorank' && s.quantityUsed === 0)
            || available.find(s => s.assignment === 'vorank');
    } else if (context === 'unterlage') {
        candidate = available.find(s => s.assignment === 'unterlage' && s.quantityUsed === 0)
            || available.find(s => s.assignment === 'unterlage');
    } else if (context === 'asplan') {
        // Wie Vorankuendigung und Unterlage: eine Pauschale je Plan — die noch
        // unbenutzte zuerst (Erstellung vor Fortschreibung).
        candidate = available.find(s => s.assignment === 'asplan' && s.quantityUsed === 0)
            || available.find(s => s.assignment === 'asplan');
    } else if (context === 'einweisung') {
        // BEWUSST OHNE die „noch unbenutzt"-Vorstufe der beiden Zeilen darueber:
        // die Einweisungsposition rechnet ueber ein KONTINGENT ab und ist ab der
        // zweiten Einweisung immer angebrochen. „Unbenutzt bevorzugen" hiesse
        // hier, ab der zweiten Buchung auf eine fremde Position zu springen.
        candidate = available.find(s => s.assignment === 'einweisung');
    }

    return [candidate ? candidate.id : (available[0]?.id || null)];
};

/**
 * Speicherschluessel der Vorauswahl. Mit `scope` (Projekt-id) JE PROJEKT.
 *
 * Ohne Projekt galt die zuletzt gebuchte Position fuer jedes Projekt auf dem
 * Geraet. Mutter und Tochter zeigen dasselbe LV (das der Mutter) — wer erst
 * die Mutter und dann die Tochter versandte, bekam bei der Tochter die
 * Mutter-Position vorbelegt und buchte sie (6290-2 #22/#23, September 2026:
 * Sunderweg 5 statt 3, Horster Dreieck 1 statt 3). Ohne `scope` bleibt der
 * alte Schluessel, fuer Aufrufer ohne gespeichertes Projekt.
 */
export const lastBillingStorageKey = (context: string, scope?: string | null): string => {
    const s = (scope || '').trim();
    return s && s !== 'temp-id' ? `last_billing_${context}:${s}` : `last_billing_${context}`;
};

/**
 * Vorauswahl einer Begehung aus dem Protokoll: die Positionen der zuletzt
 * gebuchten Begehung DIESES Projekts — bei einer Aufteilung alle Positionen
 * dieses Berichts, in Protokollreihenfolge.
 *
 *  - Tochter (`childProjectId` gesetzt): ihre Buchungen stehen im Protokoll
 *    der MUTTER — mit `RPT-CHILD-<Tochter>-<Nr>` oder in der Altform
 *    `Tochter-Projekt "<Name>": …` (`istBuchungDerTochter`, dafuer
 *    `childProjectName`). Gibt es keine, die Positionen, die beim Verknuepfen
 *    von DIESER Tochter an die Mutter gewandert sind (`transferredFrom`) — nie
 *    eine Position der Mutter.
 *  - Sonst: eigene Berichte (`RPT-…`), nie eine Tochterzeile, auch nicht in
 *    der Altform (`istKindBuchung`).
 *
 * Die Altform traegt oft `RPT-all-<Nr>` — so stehen die Tochterberichte im
 * Protokoll der Mutter, wenn sie per SQL umgehaengt wurden. Nur ueber
 * `RPT-CHILD-` gesucht, fand die Tochter dort nichts und der Dialog schlug die
 * erste Begehungsposition der Mutter vor (5606, 14.10.2026: DGUV-Begehung der
 * Tochter als Sicherheitsbegehung gebucht).
 *
 * Buchhaltungszeilen (BU-/VZ-/RN-/MB-/ALT-) belegen keine Wahl, Zeilen auf
 * Positionen ausserhalb von `services` (Zusatzleistungen) auch nicht. Ob eine
 * Id gerade gesperrt ist, entscheidet `pickInitialBillingIds`.
 */
export const preferredInspectionServiceIds = (
    services: ServiceItem[],
    serviceLog: ServiceTransaction[] | undefined,
    childProjectId: string | null,
    childProjectName?: string | null,
): string[] => {
    const gehoert = (e: ServiceTransaction) => childProjectId
        ? istBuchungDerTochter(e, childProjectId, childProjectName)
        : typeof e.reportRef === 'string' && e.reportRef.startsWith('RPT-') && !istKindBuchung(e);
    // Derselbe Bericht: ueber den Ref; eine Altzeile ohne Ref steht fuer sich.
    const bericht = (e: ServiceTransaction) => e.reportRef || `id:${e.id}`;

    const zeit = (e: ServiceTransaction) => {
        const t = Date.parse(e.bookedAt || e.date || '');
        return Number.isFinite(t) ? t : -Infinity;
    };

    // Nur Zeilen auf angebotenen Positionen: eine Zusatzleistung traegt den
    // Ref ihres Berichts mit, und eine spaet nachgetragene zu einem ALTEN
    // Bericht machte sonst diesen Bericht zum „letzten".
    const angeboten = new Set(services.map(s => s.id));
    const eigene = (serviceLog || []).filter(e =>
        !!e?.serviceId && angeboten.has(e.serviceId)
        && gehoert(e)
        && !isAccountingEntry(e)
        && !String(e.bookingReference || '').startsWith('ALT-'));

    let letzte: ServiceTransaction | undefined;
    for (const e of eigene) {
        if (!letzte || zeit(e) > zeit(letzte)) letzte = e;
    }
    if (letzte) {
        const ids: string[] = [];
        for (const e of eigene) {
            if (bericht(e) === bericht(letzte) && !ids.includes(e.serviceId)) ids.push(e.serviceId);
        }
        return ids;
    }

    if (childProjectId) {
        return services
            .filter(s => s.transferredFrom?.projectId === childProjectId)
            .map(s => s.id);
    }
    return [];
};

/**
 * Muss der Versender einer Tochter die Begehungsposition bewusst waehlen?
 *
 * Sonst griffe `pickInitialBillingIds` auf die erste freie Position zurueck —
 * bei einer Tochter ist das eine Position der Mutter, und ein Klick auf
 * „Bestätigen" buchte die Begehung dorthin (5606, 14.10.2026). Gezaehlt wird
 * wie dort nur, was nicht gesperrt ist:
 *  - eine bekannte Position der Tochter ist frei → nein, sie wird vorgewaehlt;
 *  - bekannt, aber alle gesperrt → ja, jede andere Vorwahl waere geraten;
 *  - nichts bekannt → ja, sobald mehr als eine Position angeboten wird (auch
 *    wenn davon nur eine frei ist: auch die waere geraten).
 */
export const childNeedsDeliberateChoice = (
    isChild: boolean,
    preferredIds: string[],
    services: ServiceItem[],
    blockedServices: Record<string, { reason: string }> = {},
): boolean => {
    if (!isChild) return false;
    const frei = services.filter(s => !blockedServices[s.id]);
    if (frei.length === 0) return false;
    if (preferredIds.some(id => frei.some(s => s.id === id))) return false;
    return preferredIds.length > 0 || services.length > 1;
};
